import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  PoBreadcrumb,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import {
  SimplesNacionalService,
  SimplesNacionalStatus
} from './simples-nacional.service';

type ArquivoUpload = { name: string; rawFile?: File };

const JOB_KEY = 'ecco-simples-nacional-job';

@Component({
  selector: 'app-simples-nacional',
  templateUrl: './simples-nacional.component.html',
  styleUrls: ['./simples-nacional.component.css'],
  standalone: false
})
export class SimplesNacionalComponent implements OnInit {
  @ViewChild('uploadPlanilha') uploadPlanilha?: PoUploadComponent;

  enviando = false;
  consultando = false;
  baixando = false;
  jobId = '';
  status: SimplesNacionalStatus | null = null;

  readonly urlPlaceholder = 'https://po-ui.io';
  readonly restrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.xlsx', '.xlsm'],
    maxFileSize: 50 * 1024 * 1024
  };

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Fiscal' },
      { label: 'Simples Nacional' }
    ]
  };

  constructor(
    private readonly simplesService: SimplesNacionalService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  ngOnInit(): void {
    const salvo = sessionStorage.getItem(JOB_KEY) ?? '';
    if (!salvo) return;
    this.jobId = salvo;
    this.atualizar();
  }

  enviar(): void {
    if (this.enviando) return;

    const arquivo = this.arquivo();
    if (!arquivo) {
      this.poNotificationService.warning('Envie a planilha .xlsx do Simples Nacional.');
      return;
    }

    this.enviando = true;
    this.simplesService.enviar(arquivo).subscribe({
      next: inicio => {
        this.enviando = false;
        if (inicio.error) {
          this.poNotificationService.error(inicio.error);
          return;
        }
        this.jobId = inicio.jobId;
        sessionStorage.setItem(JOB_KEY, inicio.jobId);
        this.status = {
          jobId: inicio.jobId,
          status: 'processando',
          arquivoPronto: false,
          empresas: (inicio.empresas ?? []).map(nome => ({
            nome,
            status: 'pendente',
            cnpjs: 0,
            comprovantes: 0,
            cnpjsConcluidos: []
          }))
        };
        this.poNotificationService.success('Planilha enviada. Use Atualizar para acompanhar os CNPJs.');
        this.uploadPlanilha?.clear();
      },
      error: err => {
        this.enviando = false;
        void this.mensagem(err).then(texto => this.poNotificationService.error(texto));
      }
    });
  }

  atualizar(): void {
    if (!this.jobId || this.consultando) return;

    this.consultando = true;
    this.simplesService.consultar(this.jobId).subscribe({
      next: status => {
        this.consultando = false;
        this.status = {
          ...status,
          empresas: (status.empresas ?? []).map(empresa => ({
            ...empresa,
            cnpjsConcluidos: empresa.cnpjsConcluidos ?? []
          }))
        };
      },
      error: err => {
        this.consultando = false;
        if (err instanceof HttpErrorResponse && err.status === 404) {
          sessionStorage.removeItem(JOB_KEY);
          this.jobId = '';
          this.status = null;
        }
        void this.mensagem(err).then(texto => this.poNotificationService.error(texto));
      }
    });
  }

  baixarModelo(): void {
    const link = document.createElement('a');
    link.href = '/assets/modelo/Modelo_Simples_Nacional.xlsx';
    link.download = 'Modelo SimplesSocial.xlsx';
    link.click();
  }

  baixar(): void {
    if (!this.jobId || !this.status?.arquivoPronto || this.baixando) return;

    this.baixando = true;
    this.simplesService.baixar(this.jobId).subscribe({
      next: response => {
        this.baixando = false;
        const blob = response.body;
        if (!blob || blob.size === 0) {
          this.poNotificationService.error('A planilha voltou vazia.');
          return;
        }
        this.salvar(blob, this.nomeArquivo(response));
      },
      error: err => {
        this.baixando = false;
        void this.mensagem(err).then(texto => this.poNotificationService.error(texto));
      }
    });
  }

  situacao(valor: string): string {
    switch (valor) {
      case 'pendente':
        return 'Pendente';
      case 'processando':
        return 'Em andamento';
      case 'concluido':
        return 'Concluído';
      case 'concluidoComErros':
        return 'Concluído com erros';
      case 'erro':
        return 'Erro';
      default:
        return valor || 'Pendente';
    }
  }

  formatarCnpj(valor: string): string {
    const digitos = (valor ?? '').replace(/\D/g, '');
    if (digitos.length !== 14) return valor;
    return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
  }

  private arquivo(): File | null {
    const files = (this.uploadPlanilha?.currentFiles ?? []) as ArquivoUpload[];
    return files.map(item => item.rawFile).find((file): file is File => !!file) ?? null;
  }

  private salvar(blob: Blob, nome: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
    URL.revokeObjectURL(url);
  }

  private nomeArquivo(response: HttpResponse<Blob>): string {
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(
      response.headers.get('content-disposition') ?? ''
    );
    if (!match) return 'simples-nacional.xlsx';
    return decodeURIComponent(match[1].replace(/"/g, ''));
  }

  private async mensagem(err: HttpErrorResponse): Promise<string> {
    const body = err?.error;
    if (body instanceof Blob) {
      const texto = await body.text();
      try {
        const json = JSON.parse(texto) as { error?: string; Error?: string };
        return json.error || json.Error || texto || 'Não foi possível consultar o Simples Nacional.';
      } catch {
        return texto || 'Não foi possível consultar o Simples Nacional.';
      }
    }
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      const json = body as { error?: string; Error?: string; title?: string };
      return json.error || json.Error || json.title || 'Não foi possível consultar o Simples Nacional.';
    }
    if (err?.status === 403) return 'Você não tem permissão para o Simples Nacional.';
    if (err?.status === 404) return 'Essa consulta não está mais disponível. Envie a planilha de novo.';
    return 'Não foi possível consultar o Simples Nacional.';
  }
}
